import { RuleListStatus } from '@wix/rules-panel-ui';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { ruleApplyTriggeredAtom } from '../../../../../../atoms/session-atoms';
/**
 * Finds the next failed item after the given item index
 */
const findNextFailedItem = (itemList, currentIndex) => {
  if (currentIndex === -1) {
    return null;
  }
  for (let i = currentIndex + 1; i < itemList.length; i++) {
    if (itemList[i].status === RuleListStatus.failed) {
      return itemList[i];
    }
  }
  return null;
};

/**
 * Determines if we should expand the next failed item
 * Returns true for: red→green (failed→passed) only
 */
const shouldExpandNextFailed = (oldStatus, newStatus) => {
  return oldStatus === RuleListStatus.failed && newStatus === RuleListStatus.passed;
};

/**
 * Determines if we should keep the current item open
 * Returns true for: red→red (failed→failed) and green→red (passed→failed)
 *
 * Note: This function exists for documentation and potential future use.
 * Currently, the "keep open" behavior is the default (no action needed).
 */
export const shouldKeepOpen = (oldStatus, newStatus) => {
  return oldStatus === RuleListStatus.failed && newStatus === RuleListStatus.failed || oldStatus === RuleListStatus.passed && newStatus === RuleListStatus.failed;
};

/**
 * Hook that automatically expands the next failed item when an apply action is triggered.
 *
 * This hook internally manages the `ruleApplyTriggeredAtom` state, including clearing it
 * after processing. Consumers only need to provide the page context and items.
 *
 * Behavior:
 * - red→green (failed→passed): Closes current item, opens next failed item (waits for pulse animation)
 * - red→red (failed→failed): Keeps current item open (no action)
 * - green→green (passed→passed): Closes current item only (immediate, no expansion)
 * - green→red (passed→failed): Keeps current item open (no action)
 *
 * @example
 * ```tsx
 * const { handlePulseComplete } = useOpenNextItem({
 *   pageId: 'page-123',
 *   items: ruleResults,
 *   setOpenItems,
 * });
 *
 * // The hook automatically listens to ruleApplyTriggeredAtom
 * // and clears it after processing
 * ```
 */
export const useOpenNextItem = _ref => {
  let {
    pageId,
    items,
    setOpenItems
  } = _ref;
  const [ruleApplyTriggered, setRuleApplyTriggered] = useAtom(ruleApplyTriggeredAtom);

  /**
   * Stores previous items to compare old vs new status after a rule apply.
   *
   * IMPORTANT: This ref is intentionally NOT updated when we're waiting for a status change.
   * When `oldStatus === newStatus` (except green→green), we keep the old ref to detect
   * when the status actually changes on the next render. The ref is only updated when:
   * 1. The trigger doesn't match this page (early return)
   * 2. The item is not found (clear and return)
   * 3. The status transition is fully processed (after handling)
   */
  const prevItemsRef = useRef(items);

  // Track pending expansion (waiting for pulse animation)
  const pendingExpandItemIdRef = useRef(null);

  // Single effect: when trigger fires, process the expansion logic
  useEffect(() => {
    if (!ruleApplyTriggered || ruleApplyTriggered.pageId !== pageId || !items.length) {
      // Update ref for next time
      prevItemsRef.current = items;
      return;
    }
    const triggeredItemId = ruleApplyTriggered.ruleId;

    // Find the item in previous items (old status) and current items (new status)
    const prevItem = prevItemsRef.current.find(item => item.id === triggeredItemId);
    const currentItem = items.find(item => item.id === triggeredItemId);
    if (!prevItem || !currentItem) {
      // Item not found in this list, clear trigger and update ref
      setRuleApplyTriggered(null);
      prevItemsRef.current = items;
      return;
    }
    const oldStatus = prevItem.status;
    const newStatus = currentItem.status;

    // Green→green: process immediately (just close, no need to wait for status change)
    if (oldStatus === RuleListStatus.passed && newStatus === RuleListStatus.passed) {
      setOpenItems(prev => {
        const newOpenItems = new Set(prev);
        newOpenItems.delete(triggeredItemId);
        return newOpenItems;
      });
      setRuleApplyTriggered(null);
      prevItemsRef.current = items;
      return;
    }

    // For other cases: wait for status to actually change
    if (oldStatus === newStatus) {
      // Status hasn't changed yet - wait for next render
      // Don't update ref, don't clear trigger (see prevItemsRef JSDoc above)
      return;
    }

    // Status changed - process the transition
    const triggeredItemIndex = items.findIndex(item => item.id === triggeredItemId);
    if (shouldExpandNextFailed(oldStatus, newStatus)) {
      // Red→green: close current item and open next failed
      setOpenItems(prev => {
        const newOpenItems = new Set(prev);
        newOpenItems.delete(triggeredItemId);
        return newOpenItems;
      });
      const nextFailedItem = findNextFailedItem(items, triggeredItemIndex);
      if (nextFailedItem) {
        // Red→green: wait for pulse animation before expanding
        pendingExpandItemIdRef.current = nextFailedItem.id;
      }
    }
    // For red→red and green→red: keep current item open (no action needed)

    // Clear trigger and update ref for next time
    setRuleApplyTriggered(null);
    prevItemsRef.current = items;
  }, [ruleApplyTriggered, pageId, items, setOpenItems, setRuleApplyTriggered]);

  // Callback for when pulse animation completes
  const handlePulseComplete = useCallback(() => {
    if (pendingExpandItemIdRef.current) {
      const itemIdToExpand = pendingExpandItemIdRef.current;
      setOpenItems(prev => new Set(prev).add(itemIdToExpand));
      pendingExpandItemIdRef.current = null;
    }
  }, [setOpenItems]);
  return {
    handlePulseComplete
  };
};
//# sourceMappingURL=use-open-next-item.js.map